/**
 * Payment-status → row-background classes.
 *
 * Used by the Payment Status accent row on QuoteCard (and any future
 * surface that needs to reflect the payment lifecycle in a background
 * fill). Keeps the mapping in one place so a new payment state or a
 * design tweak lands in a single edit.
 *
 * Palette:
 *   in-progress (funds moving)     → amber
 *   paid (funds settled)           → green
 *   terminal negative (dead)       → red
 *   anything unrecognized          → neutral gray
 *
 * Both `bg` and `border` are returned so callers that use the shared
 * "border-t + rounded" row shape keep the top border tinted to match
 * the fill instead of clashing against a default gray line.
 */

export const paymentStatusRowConfig = {
  // In-progress (funds are being moved but not yet cleared)
  PRE_AUTHORIZED:            { bg: 'bg-amber-50', border: 'border-amber-100' },
  WIRE_TRANSFER_IN_PROGRESS: { bg: 'bg-amber-50', border: 'border-amber-100' },
  PAYMENT_PROCESSING:        { bg: 'bg-amber-50', border: 'border-amber-100' },

  // Paid (funds settled with the merchant / operator)
  CAPTURED:             { bg: 'bg-green-50', border: 'border-green-100' },
  SETTLED_VIA_PASSPORT: { bg: 'bg-green-50', border: 'border-green-100' },

  // Terminal negative (payment won't happen)
  CANCELLED: { bg: 'bg-red-50', border: 'border-red-100' },
  REJECTED:  { bg: 'bg-red-50', border: 'border-red-100' },
};

const NEUTRAL = { bg: 'bg-gray-100', border: 'border-gray-100' };

/**
 * Resolve a payment status to its row-background classes. Case-tolerant
 * (some endpoints title-case the enum); unrecognized statuses fall back
 * to a neutral gray so a new state doesn't render as untinted.
 */
export const getPaymentStatusRowConfig = (status) => {
  const key = String(status || '').toUpperCase();
  return paymentStatusRowConfig[key] || NEUTRAL;
};
